import React, { useState, useEffect, useRef, useCallback } from 'react';

interface RangeSliderProps {
  min: number | string;
  max: number | string;
  initialMin?: number;
  initialMax?: number;
  suffix?: string;
  onChange: (min: number, max: number) => void;
  debounceMs?: number;
}

export const RangeSlider: React.FC<RangeSliderProps> = ({
  min,
  max,
  initialMin,
  initialMax,
  onChange,
  debounceMs = 2000,
}) => {
  const [minValue, setMinValue] = useState(initialMin || min);
  const [maxValue, setMaxValue] = useState(initialMax || max);
  const debounceTimerRef = useRef<number | null>(null);
  let currentMin: number | undefined = 0;
  let currentMax: number | undefined = 0;
  // Update values when min/max props change
  useEffect(() => {
    setMinValue(initialMin || min);
    setMaxValue(initialMax || max);
    currentMax = initialMax;
    currentMin = initialMin;
  }, [min, max, initialMin, initialMax]);

  // Debounced onChange handler
  const debouncedOnChange = useCallback(
    (newMin: number, newMax: number) => {
      if (debounceTimerRef.current) {
        window.clearTimeout(debounceTimerRef.current);
      }

      debounceTimerRef.current = window.setTimeout(() => {
        onChange(newMin, newMax);
      }, 0);
    },
    [onChange, debounceMs]
  );

  // Clean up timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        window.clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // Handle min slider change
  const handleMinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newMin = e.target.value ? parseInt(e.target.value) : "";
    if (newMin >= maxValue) {
      // alert("Min value can't exceed max value");
      return;
    }

    setMinValue(newMin);
  };

  const submitChange = () => {
    if (minValue == currentMin && maxValue == currentMax) {
      // alert("Min value can't exceed max value");
      return;
    }

    if (maxValue < minValue || minValue > maxValue) {
      return;
    }
    debouncedOnChange(minValue as number, maxValue as number);
  };

  // Handle max slider change
  const handleMaxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newMax = e.target.value ? parseInt(e.target.value) : "";
    setMaxValue(newMax);
  };

  return (
    <div className="py-1 space-y-4">
      <div className="space-y-4">
        {/* Range Inputs Row */}
        <div className="flex items-center space-x-4">
          {/* Min Input */}
          <div className="flex-1">
            <div className="flex items-center">
              <input
                name="range-input-min"
                type="text"
                value={minValue}
                onChange={handleMinChange}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
          <span className="text-sm text-gray-600">do</span>
          {/* Max Input */}
          <div className="flex-1">
            <div className="flex items-center">
              <input
                type="text"
                name="range-input-max"
                value={maxValue}
                onChange={handleMaxChange}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>

        <button
          type="button"
          name="range-slider-button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            submitChange();
          }}
          className="w-full btn-cart text-sm py-1 px-3"
        >
          Filtriraj
        </button>
      </div>
    </div>
  );
};
